/**
 * Shared sport / category classifier used by:
 *   - `/drops` (PlayPage) — filters drops into sport-specific chips
 *   - `/marketplace` (Swap Spot) — filters listings by same category
 *   - anywhere else we surface a "category" concept
 *
 * Rules:
 *   - Each option has a `matches(text)` predicate. First match wins.
 *   - TCG is tested BEFORE nonsport so that a card game like
 *     "Star Wars Unlimited" beats plain "Star Wars" trading cards.
 *   - The `all` bucket is always the first entry.
 *   - Keywords in the nonsport regex intentionally exclude generic
 *     terms like "gaming" / "music" / "entertainment" that produce
 *     false positives against BLK's own marketing copy.
 */

export const SPORT_OPTIONS = [
  { key: "all",    label: "All",    matches: () => true },
  { key: "nfl",    label: "NFL",    matches: (t) => /\b(nfl|football)\b/i.test(t) && !/\b(soccer|fifa|premier\s?league|world\s?cup)\b/i.test(t) },
  { key: "nba",    label: "NBA",    matches: (t) => /\b(nba|basketball)\b/i.test(t) && !/\bwnba\b/i.test(t) },
  { key: "wnba",   label: "WNBA",   matches: (t) => /\bwnba\b/i.test(t) },
  { key: "mlb",    label: "MLB",    matches: (t) => /\b(mlb|baseball|bowmen|topps\s?baseball|topps\s?chrome\s?baseball)\b/i.test(t) },
  { key: "nhl",    label: "NHL",    matches: (t) => /\b(nhl|hockey)\b/i.test(t) },
  { key: "soccer", label: "Soccer", matches: (t) => /\b(soccer|fifa|premier\s?league|world\s?cup|euro|champions\s?league|la\s?liga|mls)\b/i.test(t) },
  { key: "ufc",    label: "UFC",    matches: (t) => /\b(ufc|mma)\b/i.test(t) },
  { key: "wwe",    label: "WWE",    matches: (t) => /\b(wwe|wrestling|aew)\b/i.test(t) },
  { key: "tcg",      label: "TCG",        matches: (t) => /\b(pok[eé]mon|magic\s*(the\s*)?gathering|\bmtg\b|yu-?gi-?oh|yugioh|digimon|lorcana|one\s?piece\s?tcg|flesh\s?and\s?blood|weiss\s?schwarz|dragon\s?ball\s?super\s?card|cardfight|vanguard|metazoo|sorcery\s?contested|star\s?wars\s?unlimited|final\s?fantasy\s?tcg|shadowverse|force\s?of\s?will|keyforge|hearthstone|altered|grand\s?archive|akora|riftbound|disney\s?lorcana)\b/i.test(t) },
  { key: "nonsport", label: "Non-Sports", matches: (t) => /\b(star\s?wars|marvel|\bmcu\b|dc\s?comics|disney|pixar|harry\s?potter|lord\s?of\s?the\s?rings|\blotr\b|game\s?of\s?thrones|\bgot\b|walking\s?dead|stranger\s?things|godzilla|jurassic|mandalorian|batman|superman|spider-?man|avengers|x-?men|transformers|garbage\s?pail|non-?sports?|allen\s?&\s?ginter|topps\s?chrome\s?(?:disney|marvel|star\s?wars)|hollywood\s?walk|kpop|k-?pop\s?trading|anime\s?trading|manga\s?trading|ma[er]lon\s?monroe|marilyn\s?monroe)\b/i.test(t) },
  { key: "otx",    label: "OTX",    matches: (t) => /\botx\b/i.test(t) },
];

// The chip strip normally hides categories whose count is 0. These
// three are ALWAYS shown so the ecosystem's category coverage is
// visible even before content exists.
export const ALWAYS_VISIBLE = new Set(["all", "tcg", "nonsport"]);

/**
 * Given a text blob (usually name + description + category), return the
 * matched category key (e.g. "tcg", "nfl", "nonsport", "other").
 */
export function classifyText(text) {
  const t = String(text || "").toLowerCase();
  for (const opt of SPORT_OPTIONS) {
    if (opt.key === "all") continue;
    try {
      if (opt.matches(t)) return opt.key;
    } catch {
      /* defensive — a broken regex must never break the UI */
    }
  }
  return "other";
}

/**
 * Convenience helpers for callers that pass a drop or marketplace listing
 * object directly instead of a flat text blob.
 */
// Valid explicit category keys an admin can stamp on a drop/listing.
const VALID_KEYS = new Set(SPORT_OPTIONS.map((o) => o.key).filter((k) => k !== "all"));

// Explicit admin-set category always wins over keyword detection.
function explicitKey(value) {
  const k = String(value || "").trim().toLowerCase();
  return VALID_KEYS.has(k) ? k : null;
}

export function classifyDrop(drop) {
  if (!drop) return "other";
  const explicit = explicitKey(drop.category) || explicitKey(drop.sport);
  if (explicit) return explicit;
  return classifyText(
    [drop.name, drop.category, drop.sport, drop.series_name, drop.description]
      .filter(Boolean)
      .join(" ")
  );
}

export function classifyListing(listing) {
  if (!listing) return "other";
  const explicit = explicitKey(listing.category) || explicitKey(listing.sport);
  if (explicit) return explicit;
  return classifyText(
    [
      listing.name,
      listing.card_name,
      listing.drop_name,
      listing.series,
      listing.series_name,
      listing.category,
      listing.sport,
      listing.rarity,
      listing.description,
    ]
      .filter(Boolean)
      .join(" ")
  );
}
